import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar";
import StatsCard from "../../components/StatsCard";
import DashboardHeader from "../../components/DashboardHeader";
import "../dashboard/dashboard.css";
import RecentEventsList from "../../components/RecentEventsList";
import CommonLoader from "../../components/CommonLoader";
import { localStorageKey } from "../../constant/strings";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import {
  setBusinessData,
  setToken,
  setUserId,
} from "../../redux/slices/userSlice";
import { getUserBusiness } from "../../services/api";
import { useDashboardData } from "../../hooks/useDashboardData";
import axios from "axios";

export default function DashboardPage() {
  const dispatch = useDispatch();
  const business = useSelector((state) => state?.user?.businessData);
  const businessId = useSelector((state) => state?.user?.businessId);
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(today);
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: "",
    endDate: today,
  });

  const {
    loading,
    error,
    sales,
    expenses,
    mileage,
    bankStatements,
    aggregates,
  } = useDashboardData(
    businessId,
    appliedFilters.startDate,
    appliedFilters.endDate,
  );

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        const token = localStorage.getItem(localStorageKey.authToken);
        if (!token) return;

        const decoded = jwtDecode(token);
        const userId = decoded?.id;

        if (!userId) return;

        dispatch(setToken(token));
        dispatch(setUserId(userId));

        // Only fetch default user business if the user hasn't selected an explicit company switch
        // (i.e. if businessData is null/empty)
        if (!business) {
          const url = `${getUserBusiness}/${userId}`;
          const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const businessData = response?.data?.business;
          if (businessData) {
            dispatch(setBusinessData(businessData));
            if (!startDate) {
              const start = businessData.financialYearStart || "";
              setStartDate(start);
              setAppliedFilters({ startDate: start, endDate: today });
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    initializeDashboard();
  }, [dispatch]); // Removed business from dependency to avoid infinite loop when setting dates

  const moneyAvailableTotal =
    aggregates.moneyInBank - aggregates.unpaidInvoices + aggregates.totalVat;

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        <div
          className="dashboard-header-container"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DashboardHeader
            title={
              business?.companyName || business?.business_name || business?.name
                ? `${business.companyName || business?.business_name || business?.name} Dashboard`
                : "Dashboard"
            }
          />

          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                backgroundColor: "white",
              }}
              onClick={() =>
                document.getElementById("start-date-input").showPicker?.()
              }
            >
              <span
                style={{ fontSize: "14px", fontWeight: 500, color: "#4b5563" }}
              >
                From:
              </span>
              <input
                id="start-date-input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#111827",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                backgroundColor: "white",
              }}
              onClick={() =>
                document.getElementById("end-date-input").showPicker?.()
              }
            >
              <span
                style={{ fontSize: "14px", fontWeight: 500, color: "#4b5563" }}
              >
                To:
              </span>
              <input
                id="end-date-input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#111827",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                }}
              />
            </div>

            <button
              onClick={() => setAppliedFilters({ startDate, endDate })}
              style={{
                backgroundColor: "#c00",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "8px 20px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#a00")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#c00")}
            >
              Apply Filter
            </button>
          </div>
        </div>

        <CommonLoader visible={loading} />

        {!loading && (
          <div className="dashboard-masonry-grid">
            {/* 1. FINANCIAL OVERVIEW TOP METRICS */}
            <div className="dashboard-widget financial-overview">
              <h3 className="widget-title">Financial Overview</h3>
              <div className="stats-inner-grid">
                <StatsCard
                  title="Total Revenue"
                  value={`£${aggregates.totalRevenue.toFixed(2)}`}
                  icon="users"
                  color="blue"
                />
                <StatsCard
                  title="Total Expenses"
                  value={`£${aggregates.totalExpenses.toFixed(2)}`}
                  icon="trending-up"
                  color="red"
                />
                <StatsCard
                  title="Net Profit"
                  value={`£${aggregates.netProfit.toFixed(2)}`}
                  icon="dollar-sign"
                  color="green"
                />
                <StatsCard
                  title="Bank Balance"
                  value={`£${aggregates.moneyInBank.toFixed(2)}`}
                  icon="file-text"
                  color="gray"
                />
              </div>
            </div>

            {/* 2. MONEY AVAILABLE */}
            <div className="dashboard-widget money-available">
              <h3 className="widget-title">Money Available</h3>
              <table className="widget-table">
                <thead>
                  <tr>
                    <th>Available funds balance statement</th>
                    <th>£ Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Money in Bank</td>
                    <td>{aggregates.moneyInBank.toFixed(2)}</td>
                  </tr>
                  {/* <tr>
                    <td>Unpaid Invoices / Unmatched cash</td>
                    <td className="text-red">
                      {aggregates.unpaidInvoices > 0
                        ? `-${aggregates.unpaidInvoices.toFixed(2)}`
                        : "0.00"}
                    </td>
                  </tr>
                  <tr>
                    <td>PAYE / NICs</td>
                    <td>0.00</td>
                  </tr> */}
                  <tr>
                    <td>Corporation Tax Previous Year</td>
                    <td>0.00</td>
                  </tr>
                  <tr>
                    <td>Corporation Tax Current Year</td>
                    <td>0.00</td>
                  </tr>
                  <tr>
                    <td>VAT</td>
                    <td>{aggregates.totalVat.toFixed(2)}</td>
                  </tr>
                  {/* <tr className="row-total">
                    <td className="text-blue">►Other Total</td>
                    <td className="text-blue">
                      {aggregates.totalVat.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td>Funds Including Unpaid Invoices</td>
                    <td>
                      {(aggregates.moneyInBank + aggregates.totalVat).toFixed(
                        2,
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Less: unpaid invoices</td>
                    <td>0.00</td>
                  </tr> */}
                  <tr className="row-total">
                    <td>Total Money Available</td>
                    <td>£{moneyAvailableTotal.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 3. BANKING WIDGET */}
            <div className="dashboard-widget banking-widget card-container-widget">
              <div className="banking-card-realistic">
                <div className="banking-card-background-overlay"></div>
                <div className="banking-card-header">
                  <h3>Bank Account</h3>
                  <div className="banking-logo">ACC-BANK</div>
                </div>

                <div className="banking-card-chip">
                  <svg
                    className="emv-chip"
                    viewBox="0 0 40 30"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      width="40"
                      height="30"
                      rx="4"
                      fill="#ffd700"
                      opacity="0.8"
                    />
                    <path
                      d="M0 10 H40 M0 20 H40 M15 0 V30 M25 0 V30 M15 15 H25"
                      stroke="#b8860b"
                      strokeWidth="1"
                      fill="none"
                    />
                  </svg>
                  <svg
                    className="contactless-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8.5 16.5c2-2 5-2 7 0" />
                    <path d="M5.5 13.5c4-4 10-4 14 0" />
                    <path d="M2.5 10.5c6-6 13-6 19 0" />
                    <circle cx="12" cy="20" r="1" fill="white" stroke="none" />
                  </svg>
                </div>

                <div className="banking-card-number">
                  <span className="mute">****</span>{" "}
                  <span className="mute">****</span>{" "}
                  <span className="mute">****</span> 1234
                </div>

                <div className="banking-card-details">
                  <div className="banking-card-name">
                    <span>Account Name</span>
                    <p>Business Current</p>
                  </div>
                  <div className="banking-card-balance">
                    <span>Balance</span>
                    <p>£{aggregates.moneyInBank.toFixed(2)}</p>
                  </div>
                </div>

                <div className="banking-card-actions">
                  <button className="btn-open-banking interactive-pill">
                    Switch to Open Banking
                  </button>
                  <p className="auth-notice">Re-authenticate every 90 days</p>
                </div>
              </div>
            </div>

            {/* 4. INVOICES WIDGET */}
            <div className="dashboard-widget invoices-widget">
              <h3 className="widget-title">
                Invoices <span className="widget-subtitle">Overview</span>
              </h3>
              <div className="mini-list-header">Latest Invoices</div>
              {sales.slice(0, 3).length > 0 ? (
                sales.slice(0, 3).map((invoice, idx) => (
                  <div
                    key={idx}
                    className="mini-list-item"
                    style={{
                      animation: `fadeInRow 0.5s ease-out forwards ${0.3 + idx * 0.15}s`,
                      opacity: 0,
                      transform: "translateY(10px)",
                    }}
                  >
                    <span>{invoice.invoiceNumber || `INV-0${idx + 1}`}</span>
                    <strong>£{invoice.gross || invoice.amount}</strong>
                  </div>
                ))
              ) : (
                <div className="empty-text">No latest invoices.</div>
              )}

              <div
                className="mini-list-header"
                style={{
                  marginTop: "1rem",
                  animationDelay: "0.8s",
                  animation: "fadeInRow 0.6s ease-out forwards",
                  opacity: 0,
                }}
              >
                Latest Payments
              </div>
              <div
                className="empty-text"
                style={{
                  animationDelay: "0.9s",
                  animation: "fadeInRow 0.6s ease-out forwards",
                  opacity: 0,
                }}
              >
                No latest payments.
              </div>
            </div>

            {/* 5. PERSONALLY PAID EXPENSES & MILEAGE */}
            <div className="dashboard-widget personal-expenses">
              <h3 className="widget-title">
                Personally Paid Expenses & Mileage
              </h3>
              {mileage.length > 0 ? (
                <table className="widget-table">
                  <tbody>
                    <tr style={{ animationDelay: "0.3s" }}>
                      <td>Total Unpaid Expenses</td>
                      <td>£{aggregates.personallyPaidExpenses.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div
                  className="empty-text"
                  style={{ marginTop: "auto", marginBottom: "auto" }}
                >
                  You have no expenses to display
                </div>
              )}
            </div>

            {/* 6. BANK (Trends & To Confirm) */}
            {/* <div className="dashboard-widget bank-trends">
              <h3 className="widget-title">
                Bank <span className="widget-subtitle">Balance Trends</span>
              </h3>
              <p style={{ fontSize: "0.85rem" }}>
                Transactions to confirm:{" "}
                <span
                  style={{
                    background: "#f59e0b",
                    color: "white",
                    padding: "0.1rem 0.4rem",
                    borderRadius: "4px",
                    fontWeight: "bold",
                  }}
                >
                  0
                </span>
              </p>
            </div> */}

            {/* 7. RECEIPT BANK WIDGET */}
            <div className="dashboard-widget receipt-bank">
              <h3 className="widget-title">
                Receipt Bank <span className="widget-subtitle">Unassigned</span>
              </h3>
              <p className="empty-text">You have 0 unassigned receipts</p>
            </div>

            {/* 8. PERSONAL TAX TRACKER */}
            <div className="dashboard-widget tax-tracker">
              <h3 className="widget-title">
                Personal Tax Tracker{" "}
                <span className="widget-subtitle">Forecast</span>
              </h3>
              <table className="widget-table">
                <tbody>
                  <tr style={{ animationDelay: "0.3s" }}>
                    <td>Total Income</td>
                    <td>{aggregates.totalRevenue.toFixed(2)}</td>
                  </tr>
                  <tr style={{ animationDelay: "0.4s" }}>
                    <td>Payment due 31st January 2026</td>
                    <td>0</td>
                  </tr>
                  <tr style={{ animationDelay: "0.5s" }}>
                    <td>Payment due 31st July 2026</td>
                    <td>0</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 9. ASSIGNMENTS */}
            <div className="dashboard-widget assignments-widget">
              <h3 className="widget-title">Assignments</h3>
              <RecentEventsList hideTitle />
            </div>

            {/* 10. DOCUMENT STORAGE */}
            <div className="dashboard-widget document-storage">
              <h3 className="widget-title">
                Document Storage <span className="widget-subtitle">Recent</span>
              </h3>
              <div className="doc-item" style={{ animationDelay: "0.2s" }}>
                <span>P60 for period end 5th April 2025</span>
                <button className="interactive-pill btn-outline">View</button>
              </div>
              <div className="doc-item" style={{ animationDelay: "0.3s" }}>
                <span>Payslip 2025 02</span>
                <button className="interactive-pill btn-outline">View</button>
              </div>
              <div className="doc-item" style={{ animationDelay: "0.4s" }}>
                <span>Payslip 2025 01</span>
                <button className="interactive-pill btn-outline">View</button>
              </div>
              <div className="doc-item" style={{ animationDelay: "0.5s" }}>
                <span>April 24 PAYE Return</span>
                <button className="interactive-pill btn-outline">View</button>
              </div>
              <button
                className="interactive-pill btn-full-width mt-3"
                style={{
                  animation: "fadeInRow 0.6s ease-out forwards 0.6s",
                  opacity: 0,
                }}
              >
                View all documents
              </button>
            </div>

            {/* 12. ACTIONS WIDGET */}
            <div className="dashboard-widget actions-widget">
              <h3
                className="widget-title"
                style={{ borderBottom: "none", marginBottom: "0.2rem" }}
              >
                Actions
              </h3>
              <table className="widget-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ animationDelay: "0.4s" }}>
                    <td
                      colSpan="4"
                      className="empty-text"
                      style={{ textAlign: "center" }}
                    >
                      No tasks to be displayed.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
